import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { MapPin, X, LocateFixed, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";

// Default center: Kuwait City
const KUWAIT_CENTER = { lat: 29.3759, lng: 47.9774 };

// The Maps JS API has no typings in this project.
const googleMaps = (): any => (window as any).google?.maps;

let mapsScript: Promise<void> | null = null;

// Load the Maps JS API once per page. Adding a second <script> (every time the
// picker mounted) makes Google warn about multiple inclusion and can break the
// maps already on the page.
function loadGoogleMaps(): Promise<void> {
  if (googleMaps()) return Promise.resolve();
  if (!mapsScript) {
    mapsScript = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        mapsScript = null;
        script.remove();
        reject(new Error("Google Maps failed to load"));
      };
      document.head.appendChild(script);
    });
  }
  return mapsScript;
}

// The picked spot is always the map center; the pin's tip sits on the dot.
function CenterPin({ className }: { className: string }) {
  return (
    <>
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary ring-2 ring-background" />
      <MapPin
        className={`pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full fill-primary text-primary-foreground drop-shadow-md ${className}`}
        strokeWidth={1.5}
      />
    </>
  );
}

interface MapPickerProps {
  latitude?: number;
  longitude?: number;
  onChange: (lat: number | undefined, lng: number | undefined) => void;
}

/**
 * A small preview map in the form; tapping it opens a full-screen picker where
 * one finger moves the map under a fixed center pin. Picking inside the page
 * was hard on phones: Google needs two fingers to pan a map on a scrollable
 * page, and a dragged marker hides under the finger.
 */
export function MapPicker({ latitude, longitude, onChange }: MapPickerProps) {
  const { t } = useTranslation();
  const [mapsReady, setMapsReady] = useState(() => !!googleMaps());
  const [detecting, setDetecting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draft, setDraft] = useState<{ lat: number; lng: number } | null>(null);
  // Elements in state, not refs: the picker's map container mounts inside a
  // portal, so the effects below must re-run when it actually appears.
  const [previewEl, setPreviewEl] = useState<HTMLDivElement | null>(null);
  const [pickerEl, setPickerEl] = useState<HTMLDivElement | null>(null);
  const previewMapRef = useRef<any>(null);
  const pickerMapRef = useRef<any>(null);

  const value = latitude && longitude ? { lat: latitude, lng: longitude } : null;

  // Load Google Maps script
  useEffect(() => {
    if (!GOOGLE_MAPS_KEY || mapsReady) return;
    let cancelled = false;
    loadGoogleMaps()
      .then(() => {
        if (!cancelled) setMapsReady(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [mapsReady]);

  // Preview: display only, so scrolling the page over it can't move the pin.
  useEffect(() => {
    const maps = googleMaps();
    if (!mapsReady || !previewEl || !maps) return;
    previewMapRef.current = new maps.Map(previewEl, {
      center: KUWAIT_CENTER,
      zoom: 11,
      disableDefaultUI: true,
      gestureHandling: "none",
      clickableIcons: false,
      keyboardShortcuts: false,
    });
    return () => {
      previewMapRef.current = null;
    };
  }, [mapsReady, previewEl]);

  // Keep the preview on the chosen spot.
  useEffect(() => {
    const map = previewMapRef.current;
    if (!map) return;
    map.setCenter(value ?? KUWAIT_CENTER);
    map.setZoom(value ? 16 : 11);
  }, [latitude, longitude, mapsReady, previewEl]);

  // Picker: one finger pans the map ("greedy"); the spot under the pin is the pick.
  useEffect(() => {
    const maps = googleMaps();
    if (!pickerOpen || !mapsReady || !pickerEl || !maps) return;
    const start = value ?? KUWAIT_CENTER;
    const map = new maps.Map(pickerEl, {
      center: start,
      zoom: value ? 17 : 12,
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: "greedy",
      clickableIcons: false,
    });
    pickerMapRef.current = map;
    setDraft(start);
    map.addListener("idle", () => {
      const center = map.getCenter();
      if (center) setDraft({ lat: center.lat(), lng: center.lng() });
    });
    // A tap moves that spot under the pin.
    map.addListener("click", (e: any) => {
      if (e.latLng) map.panTo(e.latLng);
    });
    return () => {
      maps.event.clearInstanceListeners(map);
      pickerMapRef.current = null;
    };
  }, [pickerOpen, mapsReady, pickerEl]);

  // The phone's back button closes the picker instead of leaving the page
  // (and the half-filled form): opening pushes a history entry for it.
  useEffect(() => {
    if (!pickerOpen) return;
    const onBack = () => setPickerOpen(false);
    window.addEventListener("popstate", onBack);
    return () => window.removeEventListener("popstate", onBack);
  }, [pickerOpen]);

  const openPicker = () => {
    window.history.pushState({ ...window.history.state, mapPicker: true }, "");
    setPickerOpen(true);
  };

  const closePicker = () => {
    setPickerOpen(false);
    if (window.history.state?.mapPicker) window.history.back();
  };

  // Browser geolocation; onFound gets the device's position.
  const detectLocation = (onFound: (lat: number, lng: number) => void) => {
    if (!navigator.geolocation) {
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        onFound(position.coords.latitude, position.coords.longitude);
        setDetecting(false);
      },
      () => {
        setDetecting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  // In the form: use the device's position straight away.
  const handleDetectLocation = () => detectLocation((lat, lng) => onChange(lat, lng));

  // In the picker: move the map there; the customer still confirms.
  const handleLocateInPicker = () =>
    detectLocation((lat, lng) => {
      pickerMapRef.current?.panTo({ lat, lng });
      pickerMapRef.current?.setZoom(17);
    });

  const handleConfirm = () => {
    if (draft) onChange(draft.lat, draft.lng);
    closePicker();
  };

  const handleClear = () => {
    onChange(undefined, undefined);
  };

  // No API key — fallback to manual input
  if (!GOOGLE_MAPS_KEY) {
    return (
      <div className="space-y-2">
        <Input
          type="text"
          placeholder={t("map.pasteMapsLink")}
          value={latitude && longitude ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` : ""}
          onChange={(e) => {
            const match = e.target.value.match(/(-?\d+\.?\d*)/g);
            if (match && match.length >= 2) {
              onChange(parseFloat(match[0]), parseFloat(match[1]));
            }
          }}
          className="h-12"
        />
        <p className="text-xs text-muted-foreground">{t("map.noApiKey")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Detect location button */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleDetectLocation}
        disabled={detecting}
        className="w-full"
        data-testid="button-detect-location"
      >
        {detecting ? (
          <Loader2 className="h-4 w-4 me-2 animate-spin" />
        ) : (
          <LocateFixed className="h-4 w-4 me-2" />
        )}
        {t("map.detectLocation")}
      </Button>

      {/* Map preview; tapping it opens the picker. The label sits at the top,
          clear of the pin and of Google's logo at the bottom. */}
      <div
        className="relative h-48 w-full overflow-hidden rounded-xl border border-border"
        data-testid="map-picker"
      >
        <div ref={setPreviewEl} className="absolute inset-0" />
        {value && <CenterPin className="h-8 w-8" />}
        <button
          type="button"
          onClick={openPicker}
          className="absolute inset-0 flex items-start justify-center p-3"
          data-testid="button-open-map-picker"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/95 px-4 py-2 text-sm font-medium shadow-md">
            <MapPin className="h-4 w-4 text-primary" />
            {value ? t("map.changeLocation") : t("map.chooseOnMap")}
          </span>
        </button>
      </div>

      {/* Location status */}
      <div className="flex items-center justify-between">
        {latitude && longitude ? (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5 text-primary" />
            {latitude.toFixed(6)}, {longitude.toFixed(6)}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{t("map.tapToSet")}</span>
        )}
        {latitude && longitude && (
          <Button type="button" variant="ghost" size="sm" onClick={handleClear}>
            <X className="h-4 w-4 me-1" />
            {t("map.clear")}
          </Button>
        )}
      </div>

      {/* Full-screen picker (a centered window from sm up) */}
      <Dialog
        open={pickerOpen}
        onOpenChange={(open) => {
          if (!open) closePicker();
        }}
      >
        <DialogPortal>
          <DialogOverlay className="z-[60]" />
          <DialogPrimitive.Content
            className="fixed inset-0 z-[60] flex flex-col bg-background animate-in fade-in duration-200 sm:m-auto sm:h-[min(44rem,calc(100dvh-4rem))] sm:w-[min(48rem,calc(100vw-4rem))] sm:overflow-hidden sm:rounded-2xl sm:border sm:shadow-2xl"
            data-testid="map-picker-dialog"
          >
            <div className="flex items-start gap-3 border-b border-border px-4 py-3">
              <div className="flex-1 space-y-1">
                <DialogTitle className="text-base">{t("map.pickerTitle")}</DialogTitle>
                <DialogDescription className="text-xs">{t("map.pickerHint")}</DialogDescription>
              </div>
              <DialogPrimitive.Close asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  aria-label={t("map.closePicker")}
                >
                  <X className="h-5 w-5" />
                </Button>
              </DialogPrimitive.Close>
            </div>

            <div className="relative flex-1">
              <div ref={setPickerEl} className="absolute inset-0" />
              <CenterPin className="h-10 w-10" />
            </div>

            <div className="flex gap-3 border-t border-border p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-12 w-12 shrink-0"
                onClick={handleLocateInPicker}
                disabled={detecting}
                aria-label={t("map.detectLocation")}
                title={t("map.detectLocation")}
                data-testid="button-picker-detect-location"
              >
                {detecting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <LocateFixed className="h-5 w-5" />
                )}
              </Button>
              <Button
                type="button"
                className="h-12 flex-1"
                onClick={handleConfirm}
                disabled={!draft}
                data-testid="button-confirm-location"
              >
                {t("map.confirmLocation")}
              </Button>
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </div>
  );
}
